// E8SpeedTimeClosedTask.js
// Zadanie zamkniete E8: stosowanie wzoru droga-predkosc-czas.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

function UnitInlineMath({ math }) {
  // Jednostka z ulamkiem (m/s) renderowana jak ulamek w odpowiedziach rownan:
  // zwykly InlineMath z \dfrac (calosc normalnie, ulamek w naturalnym rozmiarze).
  return <InlineMath math={math} />;
}

function pick(values) {
  return values[Math.floor(Math.random() * values.length)];
}

function shuffle(values) {
  const result = [...values];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function createOptions(correctTime, distance, speed) {
  const candidates = [
    correctTime,
    correctTime * speed,
    Math.max(1, Math.floor(distance / Math.max(1, correctTime / 2))),
    distance * speed,
    correctTime + speed,
    correctTime * 10,
  ];

  const unique = [];
  candidates.forEach((value) => {
    if (Number.isInteger(value) && value > 0 && !unique.includes(value)) {
      unique.push(value);
    }
  });

  while (unique.length < 4) {
    const value = correctTime + unique.length * speed;
    if (!unique.includes(value)) unique.push(value);
  }

  const values = shuffle(unique.slice(0, 4));
  return {
    options: values.map((value, index) => ({
      label: LETTERS[index],
      value,
    })),
    correctIndex: values.indexOf(correctTime),
  };
}

function createOriginalTask() {
  const options = [50, 20, 500, 200].map((value, index) => ({
    label: LETTERS[index],
    value,
  }));

  return {
    type: "E8SpeedTimeClosedTask",
    data: {
      distance: 100,
      speed: 5,
      time: 20,
      options,
      correctIndex: 1,
    },
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 9,
    },
    tags: ["e8", "liczby_i_dzialania", "jednostki", "predkosc_droga_czas"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  const speed = pick([2, 4, 5, 8, 10]);
  const time = pick([12, 15, 18, 20, 24, 25, 30, 40]);
  const distance = speed * time;
  const { options, correctIndex } = createOptions(time, distance, speed);

  return {
    type: "E8SpeedTimeClosedTask",
    data: {
      distance,
      speed,
      time,
      options,
      correctIndex,
    },
    source: { variant: "random", basedOn: "E8_2025_MAJ_9" },
    tags: ["e8", "liczby_i_dzialania", "jednostki", "predkosc_droga_czas"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Rowerzysta pokonał odcinek drogi o długości{" "}
        <InlineMath math={`${data.distance}\\ \\text{m}`} /> z prędkością{" "}
        <UnitInlineMath math={`${data.speed}\\ \\dfrac{\\text{m}}{\\text{s}}`} />.
      </p>
      <p>
        <strong>
          Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
        </strong>
      </p>
      <p>Rowerzysta pokonał ten odcinek drogi w czasie</p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: "8px",
        }}
      >
        {data.options.map((option) => (
          <div key={option.label}>
            <ChoiceOptionMath letter={option.label} math={`${option.value}\\ \\text{sekund}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  return (
    <ABCDInput
      options={data.options.map((option, index) => ({
        value: index,
        label: option.label,
        textNode: option.label,
      }))}
      value={choiceIndex}
      onChange={(index) => {
        const option = data.options[index];
        onChange({
          choiceIndex: index,
          label: option?.label,
          value: option?.value,
        });
      }}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer?.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const option = data.options[data.correctIndex];
  return (
    <ChoiceOptionMath letter={option.label} math={`${option.value}\\ \\text{sekund}`} />
  );
}

export function renderUserAnswer(userAnswer, task) {
  const option = task?.data?.options?.[userAnswer?.choiceIndex];
  return option ? (
    <ChoiceOptionMath letter={option.label} math={`${option.value}\\ \\text{sekund}`} />
  ) : (
    <i>(brak odpowiedzi)</i>
  );
}

export function renderExplanation({ data }) {
  const correctOption = data.options[data.correctIndex];

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Korzystamy z zależności: droga to prędkość
        pomnożona przez czas.
      </p>
      <BlockMath math={"s=v \\cdot t"} />
      <p>
        <strong>Krok 2.</strong> Aby obliczyć czas, dzielimy długość drogi przez
        prędkość.
      </p>
      <BlockMath
        math={`t=\\frac{s}{v}=\\frac{${data.distance}}{${data.speed}}`}
      />
      <p>
        <strong>Krok 3.</strong> Wykonujemy dzielenie. Sprawdzamy je mnożeniem:
      </p>
      <BlockMath
        math={`${data.distance}:${data.speed}=${data.time},\\qquad ${data.speed}\\cdot${data.time}=${data.distance}`}
      />
      <p>
        <strong>Krok 4.</strong> Rowerzysta jechał przez{" "}
        <InlineMath math={`${data.time}`} /> sekund, więc poprawna jest
        odpowiedź{" "}
        <ChoiceOptionMath letter={correctOption.label} math={`${correctOption.value}\\ \\text{sekund}`} />
        .
      </p>
    </div>
  );
}
